import React, { useState, useEffect, useRef, useCallback } from "react";
import Modal from "./Modal";
import { fetchWithAuth } from "../../utils/api";
import { swalError } from "../../utils/swalAlert";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

const formatLatexText = (text) => {
  if (!text || typeof text !== "string") return text;
  let processed = text;
  if (!processed.includes("$") && !processed.includes("\\(") && !processed.includes("\\[")) {
    processed = processed.split(" ").map((word) => {
      if (/(\\[a-zA-Z]+|\^|_)/.test(word)) {
        return `$${word}$`;
      }
      return word;
    }).join(" ");
  }
  processed = processed.replace(/(^|[^\\])\$([^$]+)\$/g, (match, before, math) => {
    if (math.includes('\\displaystyle')) return match;
    return `${before}$\\displaystyle ${math}$`;
  });
  processed = processed.replace(/\\\((.*?)\\\)/g, (match, math) => {
    if (math.includes('\\displaystyle')) return match;
    return `\\(\\displaystyle ${math}\\)`;
  });
  return processed;
};

const computeCropFromBoundingBox = (bbox, naturalWidth, naturalHeight) => {
  if (!bbox || !Array.isArray(bbox) || bbox.length !== 4) {
    return { x: 10, y: 10, width: 80, height: 80 };
  }

  const [b0, b1, b2, b3] = bbox.map(Number);

  // If any coordinate is > 1000, it is definitely raw pixel coordinates [x0, y0, x1, y1] from OCR
  const isPixelCoords = b0 > 1000 || b1 > 1000 || b2 > 1000 || b3 > 1000;

  if (isPixelCoords) {
    const W = naturalWidth || 1786;
    const H = naturalHeight || 2525;
    const left = Math.min(b0, b2);
    const top = Math.min(b1, b3);
    const right = Math.max(b0, b2);
    const bottom = Math.max(b1, b3);

    const x = Math.max(0, Math.min(100, (left / W) * 100));
    const y = Math.max(0, Math.min(100, (top / H) * 100));
    const width = Math.max(2, Math.min(100 - x, ((right - left) / W) * 100));
    const height = Math.max(2, Math.min(100 - y, ((bottom - top) / H) * 100));

    return { x, y, width, height };
  }

  // Otherwise, it's normalized [ymin, xmin, ymax, xmax] in 0..1000
  const ymin = Math.min(b0, b2);
  const xmin = Math.min(b1, b3);
  const ymax = Math.max(b0, b2);
  const xmax = Math.max(b1, b3);

  const x = xmin / 10;
  const y = ymin / 10;
  const width = (xmax - xmin) / 10;
  const height = (ymax - ymin) / 10;

  return {
    x: Math.max(0, Math.min(100, x)),
    y: Math.max(0, Math.min(100, y)),
    width: Math.max(2, Math.min(100 - x, width)),
    height: Math.max(2, Math.min(100 - y, height)),
  };
};

export default function QuestionDiagramResizeModal({
  isOpen,
  onClose,
  question,
  serverUrl,
  onSaveSuccess,
  apiEndpoint, // optional – overrides the default question-bank endpoint
  showPreview = false,
  isLibraryQuestion = false,
  onEditQuestion,
  onInvalidQuestion,
  questionIndexMap,
  defaultNoCrop = false,
  defaultRemoveBg = false,
}) {
  const baseImgSrc = question?.diagramPageFileURL || question?.diagramFileURL || question?.imageURL;
  const isUsingPageFile = Boolean(question?.diagramPageFileURL);

  const [crop, setCrop] = useState({ x: 10, y: 10, width: 80, height: 80 });
  const [isImgLoading, setIsImgLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [displayZoom, setDisplayZoom] = useState(1);
  const [displayCenter, setDisplayCenter] = useState({ x: 50, y: 50 });
  const [imageAspectRatio, setImageAspectRatio] = useState(1);

  // --- Rotation / Straighten state ---
  const [rotation, setRotation] = useState(0);

  // --- Background Removal state ---
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [bgRemoved, setBgRemoved] = useState(false);
  const [processedImageUrl, setProcessedImageUrl] = useState(null);
  const [brightnessThreshold, setBrightnessThreshold] = useState(215);

  // --- Eraser state ---
  const [eraseMode, setEraseMode] = useState(false);
  const [eraserSize, setEraserSize] = useState(10);
  const [eraseStrokes, setEraseStrokes] = useState([]);
  const [erasedImageUrl, setErasedImageUrl] = useState(null);
  const [brushCursorPos, setBrushCursorPos] = useState(null); // {x, y} screen coords
  const [isApplyingErase, setIsApplyingErase] = useState(false);
  const eraseMaskCanvasRef = useRef(null);
  const isErasingRef = useRef(false);
  const currentStrokeRef = useRef([]);

  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const outerRef = useRef(null);
  const getQuestionNumber = () => {
    if (!question) return "";
    if (question.questionNumber) {
      return question.questionNumber;
    }
    if (questionIndexMap) {
      if (questionIndexMap.has(question)) {
        return questionIndexMap.get(question) + 1;
      }
      if (question._id && questionIndexMap.has(question._id)) {
        return questionIndexMap.get(question._id) + 1;
      }
    }
    if (question._originalIndex !== undefined) {
      return question._originalIndex + 1;
    }
    return "";
  };

  // Zoom towards the center of the current bounding box
  const handleZoom = (delta) => {
    setDisplayZoom((prev) => Math.min(5, Math.max(0.5, parseFloat((prev + delta).toFixed(2)))));
  };

  // Auto-calculate zoom and center based on crop
  useEffect(() => {
    if (!isImgLoading && crop && crop.width > 0 && crop.height > 0) {
      const padX = 15; // 15% padding
      const padY = 15;
      const targetZoomX = 100 / (crop.width + padX);
      const targetZoomY = 100 / (crop.height + padY);
      let z = Math.min(targetZoomX, targetZoomY);
      z = Math.max(0.5, Math.min(5, z)); // clamp zoom between 0.5 and 5
      
      setDisplayZoom(parseFloat(z.toFixed(2)));
      setDisplayCenter({
        x: crop.x + crop.width / 2,
        y: crop.y + crop.height / 2
      });
    }
  }, [crop, isImgLoading]);

  const prevSrcRef = useRef(null);
  const prevIdRef = useRef(null);
  const prevIsOpenRef = useRef(false);

  // Initialize crop from diagramBoundingBox when modal opens or question changes
  useEffect(() => {
    if (isOpen && question) {
      const newSrc = baseImgSrc;
      const newId = question._id || question.questionId;
      const justOpened = !prevIsOpenRef.current && isOpen;
      
      const shouldReset = justOpened || prevSrcRef.current !== newSrc || prevIdRef.current !== newId;
      
      if (shouldReset) {
        setIsImgLoading(true);
        setDisplayZoom(1);
        setImageAspectRatio(1);
        setRotation(0);
        setBgRemoved(false);
        setIsRemovingBg(false);
        setBrightnessThreshold(215);
        setEraseMode(false);
        setEraseStrokes([]);
        setBrushCursorPos(null);
        if (eraseMaskCanvasRef.current) {
          const ctx = eraseMaskCanvasRef.current.getContext("2d");
          ctx.clearRect(0, 0, eraseMaskCanvasRef.current.width, eraseMaskCanvasRef.current.height);
        }
        currentStrokeRef.current = [];
        isErasingRef.current = false;
        if (erasedImageUrl) {
          URL.revokeObjectURL(erasedImageUrl);
          setErasedImageUrl(null);
        }
        if (processedImageUrl) {
          URL.revokeObjectURL(processedImageUrl);
          setProcessedImageUrl(null);
        }

        if (defaultNoCrop) {
          setCrop({ x: 0, y: 0, width: 100, height: 100 });
        } else if (
          isUsingPageFile &&
          question.diagramBoundingBox &&
          Array.isArray(question.diagramBoundingBox) &&
          question.diagramBoundingBox.length === 4
        ) {
          const nw = imgRef.current?.naturalWidth;
          const nh = imgRef.current?.naturalHeight;
          setCrop(computeCropFromBoundingBox(question.diagramBoundingBox, nw, nh));
        } else {
          // If we are using the cropped diagram itself as fallback, default to crop selection of the whole image
          setCrop(isUsingPageFile ? { x: 10, y: 10, width: 80, height: 80 } : { x: 0, y: 0, width: 100, height: 100 });
        }
      }

      prevSrcRef.current = newSrc;
      prevIdRef.current = newId;
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, question, processedImageUrl, erasedImageUrl, defaultNoCrop, baseImgSrc, isUsingPageFile]);

  const handleImageLoad = (e) => {
    setIsImgLoading(false);
    const img = e?.currentTarget || imgRef.current;
    if (img) {
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      const aspect = nw / nh;
      if (aspect && !isNaN(aspect)) {
        setImageAspectRatio(aspect);
      }
      if (
        isUsingPageFile &&
        !defaultNoCrop &&
        question?.diagramBoundingBox &&
        Array.isArray(question.diagramBoundingBox) &&
        question.diagramBoundingBox.length === 4
      ) {
        setCrop(computeCropFromBoundingBox(question.diagramBoundingBox, nw, nh));
      }
    }
  };

  const handleMouseDown = (e, action) => {
    e.preventDefault();
    setIsDragging(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const startCrop = { ...crop };

    if (!containerRef.current) return;
    const startRect = containerRef.current.getBoundingClientRect();
    const containerWidth = startRect.width;
    const containerHeight = startRect.height;

    const handleMouseMove = (moveEvent) => {
      const deltaX = ((moveEvent.clientX - startX) / containerWidth) * 100;
      const deltaY = ((moveEvent.clientY - startY) / containerHeight) * 100;

      let newCrop = { ...startCrop };

      if (action === "move") {
        newCrop.x = Math.max(0, Math.min(100 - startCrop.width, startCrop.x + deltaX));
        newCrop.y = Math.max(0, Math.min(100 - startCrop.height, startCrop.y + deltaY));
      } else {
        // North
        if (action.includes("n")) {
          const newY = Math.max(0, Math.min(startCrop.y + startCrop.height - 2, startCrop.y + deltaY));
          newCrop.height = startCrop.height + (startCrop.y - newY);
          newCrop.y = newY;
        }
        // South
        if (action.includes("s")) {
          newCrop.height = Math.max(2, Math.min(100 - startCrop.y, startCrop.height + deltaY));
        }
        // West
        if (action.includes("w")) {
          const newX = Math.max(0, Math.min(startCrop.x + startCrop.width - 2, startCrop.x + deltaX));
          newCrop.width = startCrop.width + (startCrop.x - newX);
          newCrop.x = newX;
        }
        // East
        if (action.includes("e")) {
          newCrop.width = Math.max(2, Math.min(100 - startCrop.x, startCrop.width + deltaX));
        }
      }

      setCrop(newCrop);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e, action) => {
    e.preventDefault();
    setIsDragging(true);
    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;
    const startCrop = { ...crop };

    if (!containerRef.current) return;
    const startRect = containerRef.current.getBoundingClientRect();
    const containerWidth = startRect.width;
    const containerHeight = startRect.height;

    const handleTouchMove = (moveEvent) => {
      const touchMove = moveEvent.touches[0];
      const deltaX = ((touchMove.clientX - startX) / containerWidth) * 100;
      const deltaY = ((touchMove.clientY - startY) / containerHeight) * 100;

      let newCrop = { ...startCrop };

      if (action === "move") {
        newCrop.x = Math.max(0, Math.min(100 - startCrop.width, startCrop.x + deltaX));
        newCrop.y = Math.max(0, Math.min(100 - startCrop.height, startCrop.y + deltaY));
      } else {
        if (action.includes("n")) {
          const newY = Math.max(0, Math.min(startCrop.y + startCrop.height - 2, startCrop.y + deltaY));
          newCrop.height = startCrop.height + (startCrop.y - newY);
          newCrop.y = newY;
        }
        if (action.includes("s")) {
          newCrop.height = Math.max(2, Math.min(100 - startCrop.y, startCrop.height + deltaY));
        }
        if (action.includes("w")) {
          const newX = Math.max(0, Math.min(startCrop.x + startCrop.width - 2, startCrop.x + deltaX));
          newCrop.width = startCrop.width + (startCrop.x - newX);
          newCrop.x = newX;
        }
        if (action.includes("e")) {
          newCrop.width = Math.max(2, Math.min(100 - startCrop.x, startCrop.width + deltaX));
        }
      }

      setCrop(newCrop);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };

    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);
  };

  // --- Background Removal: threshold-based white pixel detection ---
  const handleRemoveBackground = useCallback(async () => {
    const imgSrc = baseImgSrc;
    if (!imgSrc) return;
    setIsRemovingBg(true);
    try {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.src = imgSrc;
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error("Failed to load image for background removal"));
      });

      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(image, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const threshold = brightnessThreshold;

      // Pass 1: Mark background pixels as transparent
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        // If pixel is above threshold on all channels → background
        if (r >= threshold && g >= threshold && b >= threshold) {
          data[i + 3] = 0; // set alpha to 0
        }
      }

      // Pass 2: Edge smoothing – soften alpha at edges between opaque and transparent
      const width = canvas.width;
      const height = canvas.height;
      const alphaOriginal = new Uint8Array(data.length / 4);
      for (let i = 0; i < alphaOriginal.length; i++) {
        alphaOriginal[i] = data[i * 4 + 3];
      }
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          const idx = y * width + x;
          const a = alphaOriginal[idx];
          if (a === 0) continue; // already transparent
          // Check if any neighbor is transparent
          const neighbors = [
            alphaOriginal[idx - 1], alphaOriginal[idx + 1],
            alphaOriginal[idx - width], alphaOriginal[idx + width],
          ];
          const transparentNeighbors = neighbors.filter(n => n === 0).length;
          if (transparentNeighbors > 0 && transparentNeighbors < 4) {
            // Edge pixel – blend alpha proportionally
            const factor = 1 - (transparentNeighbors / 4) * 0.5;
            data[idx * 4 + 3] = Math.round(a * factor);
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);

      const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"));
      if (processedImageUrl) URL.revokeObjectURL(processedImageUrl);
      const url = URL.createObjectURL(blob);
      setProcessedImageUrl(url);
      setBgRemoved(true);
    } catch (err) {
      console.error("Background removal error:", err);
      swalError("Failed to remove background from image.");
    } finally {
      setIsRemovingBg(false);
    }
  }, [baseImgSrc, brightnessThreshold, processedImageUrl]);

  const handleUndoBgRemoval = useCallback(() => {
    if (processedImageUrl) URL.revokeObjectURL(processedImageUrl);
    setProcessedImageUrl(null);
    setBgRemoved(false);
  }, [processedImageUrl]);

  // --- Rotation handlers ---
  const handleRotate90 = (direction) => {
    setRotation(prev => {
      const next = prev + (direction === "cw" ? 90 : -90);
      return next % 360;
    });
  };

  const handleFineTuneRotation = (delta) => {
    setRotation(prev => {
      const next = Math.round((prev + delta) * 10) / 10;
      return Math.max(-220, Math.min(220, next));
    });
  };

  const handleResetRotation = () => setRotation(0);

  // --- Eraser helpers ---
  const ensureEraseMask = useCallback(() => {
    if (!imgRef.current) return null;
    let canvas = eraseMaskCanvasRef.current;
    if (!canvas) return null;
    
    const cw = imgRef.current.clientWidth;
    const ch = imgRef.current.clientHeight;
    if (cw === 0 || ch === 0) return canvas;

    const targetWidth = cw * 2;
    const targetHeight = ch * 2;

    if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
    }
    return canvas;
  }, []);

  // Get the currently displayed image source URL
  const getDisplayedImageSrc = useCallback(() => {
    return bgRemoved && processedImageUrl
      ? processedImageUrl
      : baseImgSrc;
  }, [bgRemoved, processedImageUrl, baseImgSrc]);

  // Apply erase mask to current image and generate a new blob URL
  const applyEraseMaskToImage = useCallback(async () => {
    const mask = eraseMaskCanvasRef.current;
    if (!mask) return;
    const imgSrc = getDisplayedImageSrc();
    if (!imgSrc) return;

    setIsApplyingErase(true);
    try {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.src = imgSrc;
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error("Failed to load image for erase"));
      });

      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(image, 0, 0);

      // Scale mask to image size and apply
      const tempMask = document.createElement("canvas");
      tempMask.width = canvas.width;
      tempMask.height = canvas.height;
      const tmCtx = tempMask.getContext("2d");
      tmCtx.drawImage(mask, 0, 0, tempMask.width, tempMask.height);
      const maskData = tmCtx.getImageData(0, 0, tempMask.width, tempMask.height);
      const srcData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < maskData.data.length; i += 4) {
        if (maskData.data[i] > 128) {
          srcData.data[i + 3] = 0;
        }
      }
      ctx.putImageData(srcData, 0, 0);

      const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"));
      if (erasedImageUrl) URL.revokeObjectURL(erasedImageUrl);
      const url = URL.createObjectURL(blob);
      setErasedImageUrl(url);
    } finally {
      setIsApplyingErase(false);
    }
  }, [getDisplayedImageSrc, erasedImageUrl]);

  const screenToMaskCoords = useCallback((clientX, clientY) => {
    if (!imgRef.current || !outerRef.current) return null;
    const img = imgRef.current;
    const mask = eraseMaskCanvasRef.current;
    if (!mask) return null;

    const imgWidth = img.clientWidth;
    const imgHeight = img.clientHeight;
    if (imgWidth === 0 || imgHeight === 0) return null;

    const outerRect = outerRef.current.getBoundingClientRect();
    const centerX = outerRect.left + outerRect.width / 2;
    const centerY = outerRect.top + outerRect.height / 2;

    const P0_x = centerX - (displayCenter.x / 100) * imgWidth * displayZoom;
    const P0_y = centerY - (displayCenter.y / 100) * imgHeight * displayZoom;

    const localX = (clientX - P0_x) / displayZoom;
    const localY = (clientY - P0_y) / displayZoom;

    const cx = imgWidth / 2;
    const cy = imgHeight / 2;
    const dx = localX - cx;
    const dy = localY - cy;

    const angleRad = (-rotation * Math.PI) / 180;
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    const unrotatedX = dx * cos - dy * sin;
    const unrotatedY = dx * sin + dy * cos;

    const imgLocalX = unrotatedX + cx;
    const imgLocalY = unrotatedY + cy;

    const pctX = imgLocalX / imgWidth;
    const pctY = imgLocalY / imgHeight;

    return { x: pctX * mask.width, y: pctY * mask.height };
  }, [displayZoom, displayCenter, rotation]);

  const drawOnMask = useCallback((x, y, brushRadius, prevX, prevY) => {
    const mask = eraseMaskCanvasRef.current;
    if (!mask) return;
    const ctx = mask.getContext("2d");
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "rgba(255,0,0,1)";
    ctx.strokeStyle = "rgba(255,0,0,1)";
    ctx.lineWidth = brushRadius * 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    ctx.beginPath();
    if (prevX !== undefined && prevY !== undefined) {
      ctx.moveTo(prevX, prevY);
      ctx.lineTo(x, y);
      ctx.stroke();
    } else {
      ctx.arc(x, y, brushRadius, 0, Math.PI * 2);
      ctx.fill();
    }
  }, []);

  const handleEraseMouseDown = useCallback((e) => {
    if (!eraseMode || !imgRef.current) return;
    // Check if click is on the image area
    const imgRect = imgRef.current.getBoundingClientRect();
    if (e.clientX < imgRect.left || e.clientX > imgRect.right ||
        e.clientY < imgRect.top || e.clientY > imgRect.bottom) return;
    e.preventDefault();
    e.stopPropagation();
    isErasingRef.current = true;
    currentStrokeRef.current = [];
    const mask = ensureEraseMask();
    if (!mask) return;
    const coords = screenToMaskCoords(e.clientX, e.clientY);
    if (!coords) return;
    
    // Calculate brush radius mathematically exactly as scale
    // displayZoom scales the img element's client dimensions
    // mask.width / imgRef.current.clientWidth is the intrinsic vs CSS layout scale
    const intrinsicScale = mask.width / imgRef.current.clientWidth;
    const brushRadius = (eraserSize / displayZoom) * intrinsicScale;
    
    drawOnMask(coords.x, coords.y, brushRadius);
    currentStrokeRef.current.push({ x: coords.x, y: coords.y });
    setBrushCursorPos({ x: e.clientX, y: e.clientY });

    const handleMove = (moveEvent) => {
      if (!isErasingRef.current) return;
      const mc = screenToMaskCoords(moveEvent.clientX, moveEvent.clientY);
      if (mc) {
        const lastPt = currentStrokeRef.current[currentStrokeRef.current.length - 1];
        drawOnMask(mc.x, mc.y, brushRadius, lastPt?.x, lastPt?.y);
        currentStrokeRef.current.push({ x: mc.x, y: mc.y });
      }
      setBrushCursorPos({ x: moveEvent.clientX, y: moveEvent.clientY });
    };

    const handleUp = async () => {
      isErasingRef.current = false;
      setBrushCursorPos(null);
      if (currentStrokeRef.current.length > 0) {
        setEraseStrokes(prev => [...prev, { points: [...currentStrokeRef.current], brushRadius }]);
      }
      currentStrokeRef.current = [];
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
  }, [eraseMode, eraserSize, ensureEraseMask, screenToMaskCoords, drawOnMask, displayZoom]);

  const handleEraseTouchStart = useCallback((e) => {
    if (!eraseMode || !imgRef.current) return;
    const touch = e.touches[0];
    const imgRect = imgRef.current.getBoundingClientRect();
    if (touch.clientX < imgRect.left || touch.clientX > imgRect.right ||
        touch.clientY < imgRect.top || touch.clientY > imgRect.bottom) return;
    e.preventDefault();
    e.stopPropagation();
    isErasingRef.current = true;
    currentStrokeRef.current = [];
    const mask = ensureEraseMask();
    if (!mask) return;
    const coords = screenToMaskCoords(touch.clientX, touch.clientY);
    if (!coords) return;
    
    const intrinsicScale = mask.width / imgRef.current.clientWidth;
    const brushRadius = (eraserSize / displayZoom) * intrinsicScale;
    drawOnMask(coords.x, coords.y, brushRadius);
    currentStrokeRef.current.push({ x: coords.x, y: coords.y });
    setBrushCursorPos({ x: touch.clientX, y: touch.clientY });

    const handleTouchMove = (moveEvent) => {
      if (!isErasingRef.current) return;
      const t = moveEvent.touches[0];
      const mc = screenToMaskCoords(t.clientX, t.clientY);
      if (mc) {
        const lastPt = currentStrokeRef.current[currentStrokeRef.current.length - 1];
        drawOnMask(mc.x, mc.y, brushRadius, lastPt?.x, lastPt?.y);
        currentStrokeRef.current.push({ x: mc.x, y: mc.y });
      }
      setBrushCursorPos({ x: t.clientX, y: t.clientY });
    };

    const handleTouchEnd = async () => {
      isErasingRef.current = false;
      setBrushCursorPos(null);
      if (currentStrokeRef.current.length > 0) {
        setEraseStrokes(prev => [...prev, { points: [...currentStrokeRef.current], brushRadius }]);
      }
      currentStrokeRef.current = [];
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };

    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);
  }, [eraseMode, eraserSize, ensureEraseMask, screenToMaskCoords, drawOnMask, displayZoom]);

  const handleEraseUndo = useCallback(async () => {
    if (eraseStrokes.length === 0) return;
    const newStrokes = eraseStrokes.slice(0, -1);
    setEraseStrokes(newStrokes);
    // Rebuild mask from remaining strokes
    const mask = eraseMaskCanvasRef.current;
    if (!mask) return;
    const ctx = mask.getContext("2d");
    ctx.clearRect(0, 0, mask.width, mask.height);
    newStrokes.forEach(stroke => {
      let prevPt = null;
      stroke.points.forEach((pt, idx) => {
        ctx.globalCompositeOperation = "source-over";
        ctx.fillStyle = "rgba(255,0,0,1)";
        ctx.strokeStyle = "rgba(255,0,0,1)";
        ctx.lineWidth = stroke.brushRadius * 2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        ctx.beginPath();
        if (idx > 0 && prevPt) {
          ctx.moveTo(prevPt.x, prevPt.y);
          ctx.lineTo(pt.x, pt.y);
          ctx.stroke();
        } else {
          ctx.arc(pt.x, pt.y, stroke.brushRadius, 0, Math.PI * 2);
          ctx.fill();
        }
        prevPt = pt;
      });
    });
    if (newStrokes.length > 0) {
      try { await applyEraseMaskToImage(); } catch (err) { console.error("Erase undo error:", err); }
    } else {
      // No strokes left — clear erased image
      if (erasedImageUrl) URL.revokeObjectURL(erasedImageUrl);
      setErasedImageUrl(null);
    }
  }, [eraseStrokes, applyEraseMaskToImage, erasedImageUrl]);

  const handleEraseReset = useCallback(() => {
    setEraseStrokes([]);
    const mask = eraseMaskCanvasRef.current;
    if (mask) {
      mask.getContext("2d").clearRect(0, 0, mask.width, mask.height);
    }
    if (erasedImageUrl) URL.revokeObjectURL(erasedImageUrl);
    setErasedImageUrl(null);
  }, [erasedImageUrl]);

  const handleSave = async () => {
    if (!imgRef.current) return;
    try {
      setIsSaving(true);

      // Load the source image (use erased > bg-removed > original)
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.src = erasedImageUrl
        ? erasedImageUrl
        : (bgRemoved && processedImageUrl
          ? processedImageUrl
          : baseImgSrc);

      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error("Failed to load full diagram page image"));
      });

      // Calculate crop dimensions based on the natural image size
      const W = image.naturalWidth;
      const H = image.naturalHeight;
      const pixelX = (crop.x / 100) * W;
      const pixelY = (crop.y / 100) * H;
      const pixelWidth = Math.max(1, Math.round((crop.width / 100) * W));
      const pixelHeight = Math.max(1, Math.round((crop.height / 100) * H));

      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = pixelWidth;
      finalCanvas.height = pixelHeight;
      const fCtx = finalCanvas.getContext("2d");

      // Draw the rotated image so that the crop box aligns with the canvas
      // This exactly matches how the Preview UI renders the image inside its container
      fCtx.translate(-pixelX, -pixelY);
      fCtx.translate(W / 2, H / 2);
      fCtx.rotate((rotation * Math.PI) / 180);
      fCtx.translate(-W / 2, -H / 2);
      fCtx.drawImage(image, 0, 0, W, H);

      finalCanvas.toBlob(
        async (blob) => {
          if (!blob) {
            swalError("Failed to generate cropped image blob.");
            setIsSaving(false);
            return;
          }

          const ymin = Math.round(crop.y * 10);
          const xmin = Math.round(crop.x * 10);
          const ymax = Math.round((crop.y + crop.height) * 10);
          const xmax = Math.round((crop.x + crop.width) * 10);
          const newBoundingBox = [ymin, xmin, ymax, xmax];

          const file = new File([blob], `cropped_diagram_${question._id || question.questionId}.png`, {
            type: "image/png",
          });

          const formData = new FormData();
          formData.append("image", file);
          // Only update diagramBoundingBox if we are using the page file (i.e. not already a diagram file)
          if (isUsingPageFile) {
            formData.append("diagramBoundingBox", JSON.stringify(newBoundingBox));
          }

          try {
            const endpoint = apiEndpoint
              ? `${serverUrl}${apiEndpoint}/${question._id || question.questionId}`
              : `${serverUrl}/api/question-bank/update-question-diagram/${question._id || question.questionId}`;
            const response = await fetchWithAuth(endpoint, {
              method: "POST",
              body: formData,
            });

            if (!response) {
              setIsSaving(false);
              return;
            }

            const data = await response.json();
            if (response.ok) {
              //swalSuccess(data.message || "Question diagram updated successfully");
              if (onSaveSuccess) {
                onSaveSuccess(
                  question._id || question.questionId,
                  data.diagramFileURL,
                  data.diagramBoundingBox || newBoundingBox
                );
              }
              onClose();
            } else {
              swalError(data.message || "Failed to update question diagram");
            }
          } catch (uploadErr) {
            console.error("Error uploading cropped diagram:", uploadErr);
            swalError("Failed to upload the updated diagram.");
          } finally {
            setIsSaving(false);
          }
        },
        "image/png"
      );
    } catch (err) {
      console.error("Error cropping image:", err);
      swalError("Error preparing image for crop.");
      setIsSaving(false);
    }
  };

  const handleNoCrop = () => {
    setDisplayZoom(1);
    setCrop({ x: 0, y: 0, width: 100, height: 100 });
  };

  const handleReset = () => {
    setDisplayZoom(1);
    setRotation(0);
    handleUndoBgRemoval();
    setBrightnessThreshold(215);
    setEraseMode(false);
    handleEraseReset();
    setBrushCursorPos(null);
    if (
      isUsingPageFile &&
      question?.diagramBoundingBox &&
      Array.isArray(question.diagramBoundingBox) &&
      question.diagramBoundingBox.length === 4
    ) {
      const nw = imgRef.current?.naturalWidth;
      const nh = imgRef.current?.naturalHeight;
      setCrop(computeCropFromBoundingBox(question.diagramBoundingBox, nw, nh));
    } else {
      setCrop(isUsingPageFile ? { x: 10, y: 10, width: 80, height: 80 } : { x: 0, y: 0, width: 100, height: 100 });
    }
  };

  // Styles for handles
  const handleStyle = (cursor, top, left, right, bottom, transform) => ({
    position: "absolute",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: "#fb923c",
    border: "1px solid #ffffff",
    cursor,
    top,
    left,
    right,
    bottom,
    transform,
    zIndex: 10,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="100%" zIndex={1100}>
      <div style={{ padding: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: "600", color: "#1f2937" }}>
            <i className="fa-solid fa-crop-simple" style={{ marginRight: "0.5rem", color: "var(--color-1)" }}></i>
            Edit Question Diagram
          </h3>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1rem", borderBottom: "1px solid #e2e8f0", paddingBottom: "1rem" }}>
          {/* Question Text (Full Width) */}
          <div style={{ width: "100%" }}>
            {question?.questionText ? (
              <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start", backgroundColor: "#f8fafc", padding: "0.75rem", borderRadius: "8px", border: "1px solid #f1f5f9" }}>
                {getQuestionNumber() && (
                  <span
                    style={{
                      background: "linear-gradient(135deg, var(--color-1), var(--color-2))",
                      color: "#fff",
                      borderRadius: "50%",
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      flexShrink: 0,
                      marginTop: "2px",
                    }}
                  >
                    {getQuestionNumber()}
                  </span>
                )}
                <div style={{ fontSize: "0.9rem", color: "#334155", lineHeight: 1.5, flex: 1, minWidth: 0 }}>
                  <ReactMarkdown
                    children={formatLatexText(question.questionText)}
                    remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                    rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                  />
                </div>
              </div>
            ) : (
              <div style={{ fontSize: "0.9rem", fontStyle: "italic", color: "#94a3b8", backgroundColor: "#f8fafc", padding: "0.75rem", borderRadius: "8px", border: "1px solid #f1f5f9" }}>
                No question text available
              </div>
            )}
          </div>

          {/* Controls (Edit, Zoom, No Crop, Reset) */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "0.5rem", flexWrap: "wrap", width: "100%" }}>
            {/* Edit Button */}             {isLibraryQuestion && onEditQuestion && (
              <button
                type="button"
                onClick={() => onEditQuestion(question)}
                style={{
                  padding: "0.5rem 0.8rem",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  backgroundColor: "#ffffff",
                  color: "var(--color-1)",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
              >
                <i className="fa-solid fa-pen" /> Edit
              </button>
            )}

            {isLibraryQuestion && onInvalidQuestion && (
              <button
                type="button"
                onClick={() => onInvalidQuestion(question)}
                style={{
                  padding: "0.5rem 0.8rem",
                  borderRadius: "6px",
                  border: question?.isInvalid ? "1px solid #fca5a5" : "1px solid #d1d5db",
                  backgroundColor: question?.isInvalid ? "rgba(239, 68, 68, 0.1)" : "#ffffff",
                  color: question?.isInvalid ? "#ef4444" : "#475569",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = question?.isInvalid ? "rgba(239, 68, 68, 0.15)" : "#f9fafb")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = question?.isInvalid ? "rgba(239, 68, 68, 0.1)" : "#ffffff")}
              >
                <i className="fa-solid fa-triangle-exclamation" /> Invalid
              </button>
            )}

            {/* Zoom Controls */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexShrink: 0 }}>
              <button
                type="button"
                title="Zoom out"
                onClick={() => handleZoom(-0.25)}
                disabled={displayZoom <= 0.5}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  background: displayZoom <= 0.5 ? "#f3f4f6" : "#ffffff",
                  color: displayZoom <= 0.5 ? "#9ca3af" : "#374151",
                  fontSize: "1.1rem",
                  fontWeight: "700",
                  cursor: displayZoom <= 0.5 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  transition: "background 0.15s",
                }}
              >
                −
              </button>
              <span
                style={{
                  minWidth: "44px",
                  textAlign: "center",
                  fontSize: "0.8rem",
                  fontWeight: "600",
                  color: "var(--color-1)",
                  background: "#ede9fe",
                  borderRadius: "5px",
                  padding: "3px 6px",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {Math.round(displayZoom * 100)}%
              </span>
              <button
                type="button"
                title="Zoom in"
                onClick={() => handleZoom(0.25)}
                disabled={displayZoom >= 5}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  background: displayZoom >= 5 ? "#f3f4f6" : "#ffffff",
                  color: displayZoom >= 5 ? "#9ca3af" : "#374151",
                  fontSize: "1.1rem",
                  fontWeight: "700",
                  cursor: displayZoom >= 5 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  transition: "background 0.15s",
                }}
              >
                +
              </button>
            </div>

            {/* No Crop Button */}
            <button
              type="button"
              onClick={handleNoCrop}
              disabled={isSaving}
              style={{
                padding: "0.5rem 0.8rem",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                backgroundColor: "#ffffff",
                color: "#374151",
                fontSize: "0.875rem",
                fontWeight: "500",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
            >
              <i className="fa fa-crop"></i> No Crop
            </button>

            {/* Reset Button */}
            <button
              type="button"
              onClick={handleReset}
              disabled={isSaving}
              style={{
                padding: "0.5rem 0.8rem",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                backgroundColor: "#ffffff",
                color: "#374151",
                fontSize: "0.875rem",
                fontWeight: "500",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
            >
              Reset
            </button>

            {/* Diagram Page Button */}
            {(() => {
              const diagramPageUrl = baseImgSrc;
              if (!diagramPageUrl) return null;
              return (
                <button
                  type="button"
                  onClick={() => window.open(diagramPageUrl, "_blank")}
                  disabled={isSaving}
                  style={{
                    padding: "0.5rem 0.8rem",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    backgroundColor: "#ffffff",
                    color: "var(--color-1)",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
                >
                  <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: "0.75rem" }}></i> Diagram Page
                </button>
              );
            })()}
          </div>

          {/* ====== Image Tools Toolbar ====== */}
          <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            alignItems: "center",
            width: "100%",
            padding: "0.75rem",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
            border: "1px solid #e2e8f0",
          }}>

            {/* --- Straighten / Rotate Section --- */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
              <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#475569", display: "flex", alignItems: "center", gap: "4px" }}>
                <i className="fa-solid fa-rotate" style={{ color: "var(--color-1)", fontSize: "0.85rem" }} />
                Straighten
              </span>

              {/* Fine tune -0.5 */}
              <button
                type="button"
                title="Rotate -0.5°"
                onClick={() => handleFineTuneRotation(-0.5)}
                disabled={isSaving}
                style={{
                  width: "28px", height: "28px", borderRadius: "6px",
                  border: "1px solid #d1d5db", background: "#fff", color: "#374151",
                  fontSize: "0.7rem", fontWeight: 700, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
              >
                -.5
              </button>

              {/* Rotation slider */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                <input
                  type="range"
                  min="-45"
                  max="45"
                  step="0.1"
                  value={rotation}
                  onChange={e => setRotation(parseFloat(e.target.value))}
                  disabled={isSaving}
                  style={{ width: "100px", accentColor: "var(--color-1)", cursor: "pointer" }}
                />
                <span style={{
                  minWidth: "48px", textAlign: "center", fontSize: "0.75rem",
                  fontWeight: 600, color: "var(--color-1)",
                  background: "#ede9fe", borderRadius: "5px", padding: "2px 6px",
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {rotation.toFixed(1)}°
                </span>
              </div>

              {/* Fine tune +0.5 */}
              <button
                type="button"
                title="Rotate +0.5°"
                onClick={() => handleFineTuneRotation(0.5)}
                disabled={isSaving}
                style={{
                  width: "28px", height: "28px", borderRadius: "6px",
                  border: "1px solid #d1d5db", background: "#fff", color: "#374151",
                  fontSize: "0.7rem", fontWeight: 700, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
              >
                +.5
              </button>

              {/* Rotate 90° CCW */}
              <button
                type="button"
                title="Rotate 90° Counter-clockwise"
                onClick={() => handleRotate90("ccw")}
                disabled={isSaving}
                style={{
                  width: "30px", height: "30px", borderRadius: "6px",
                  border: "1px solid #d1d5db", background: "#fff", color: "#374151",
                  fontSize: "0.8rem", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
              >
                <i className="fa-solid fa-rotate-left" />
              </button>

              {/* Rotate 90° CW */}
              <button
                type="button"
                title="Rotate 90° Clockwise"
                onClick={() => handleRotate90("cw")}
                disabled={isSaving}
                style={{
                  width: "30px", height: "30px", borderRadius: "6px",
                  border: "1px solid #d1d5db", background: "#fff", color: "#374151",
                  fontSize: "0.8rem", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
              >
                <i className="fa-solid fa-rotate-right" />
              </button>

              {/* Reset rotation */}
              {rotation !== 0 && (
                <button
                  type="button"
                  title="Reset rotation"
                  onClick={handleResetRotation}
                  disabled={isSaving}
                  style={{
                    padding: "4px 8px", borderRadius: "6px",
                    border: "1px solid #fca5a5", background: "#fff", color: "#ef4444",
                    fontSize: "0.7rem", fontWeight: 600, cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
                  onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >
                  0°
                </button>
              )}
            </div>

            {/* Divider */}
            <div style={{ width: "1px", height: "28px", background: "#cbd5e1", flexShrink: 0 }} />

            {/* --- Background Removal Section --- */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
              <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#475569", display: "flex", alignItems: "center", gap: "4px" }}>
                <i className="fa-solid fa-eraser" style={{ color: "var(--color-1)", fontSize: "0.85rem" }} />
                Background
              </span>

              {/* Threshold slider */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                <span style={{ fontSize: "0.65rem", color: "#94a3b8" }}>Sensitivity</span>
                <input
                  type="range"
                  min="0"
                  max="255"
                  step="1"
                  value={brightnessThreshold}
                  onChange={e => setBrightnessThreshold(parseInt(e.target.value))}
                  disabled={isSaving || isRemovingBg}
                  style={{ width: "70px", accentColor: "var(--color-1)", cursor: "pointer" }}
                  title={`Threshold: ${brightnessThreshold} — Lower = more aggressive removal`}
                />
                <span style={{
                  fontSize: "0.65rem", color: "#64748b", fontVariantNumeric: "tabular-nums",
                  minWidth: "24px", textAlign: "center",
                }}>
                  {brightnessThreshold}
                </span>
              </div>

              {/* Remove BG button */}
              <button
                type="button"
                onClick={handleRemoveBackground}
                disabled={isSaving || isRemovingBg}
                style={{
                  padding: "5px 12px", borderRadius: "6px",
                  border: bgRemoved ? "1px solid #86efac" : "1px solid #d1d5db",
                  background: bgRemoved ? "#f0fdf4" : "#fff",
                  color: bgRemoved ? "#16a34a" : "#374151",
                  fontSize: "0.78rem", fontWeight: 500, cursor: isRemovingBg ? "wait" : "pointer",
                  display: "flex", alignItems: "center", gap: "4px",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => { if (!isRemovingBg) e.currentTarget.style.background = bgRemoved ? "#dcfce7" : "#f1f5f9"; }}
                onMouseLeave={e => { e.currentTarget.style.background = bgRemoved ? "#f0fdf4" : "#fff"; }}
              >
                {isRemovingBg ? (
                  <><i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "0.75rem" }} /> Removing...</>
                ) : (
                  <><i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: "0.75rem" }} /> {bgRemoved ? "Re-apply" : "Remove BG"}</>
                )}
              </button>

              {/* Undo BG removal */}
              {bgRemoved && (
                <button
                  type="button"
                  onClick={handleUndoBgRemoval}
                  disabled={isSaving}
                  style={{
                    padding: "5px 10px", borderRadius: "6px",
                    border: "1px solid #fca5a5", background: "#fff", color: "#ef4444",
                    fontSize: "0.75rem", fontWeight: 500, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "4px",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
                  onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >
                  <i className="fa-solid fa-undo" style={{ fontSize: "0.7rem" }} /> Undo
                </button>
              )}
            </div>

            {/* Divider */}
            <div style={{ width: "1px", height: "28px", background: "#cbd5e1", flexShrink: 0 }} />

            {/* --- Eraser Section --- */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
              {/* Erase mode toggle */}
              <button
                type="button"
                onClick={() => setEraseMode(prev => !prev)}
                disabled={isSaving}
                style={{
                  padding: "5px 12px", borderRadius: "6px",
                  border: eraseMode ? "1.5px solid var(--color-1)" : "1px solid #d1d5db",
                  background: eraseMode ? "linear-gradient(135deg, rgba(99,102,241,0.12), rgba(168,85,247,0.12))" : "#fff",
                  color: eraseMode ? "var(--color-1)" : "#374151",
                  fontSize: "0.78rem", fontWeight: eraseMode ? 600 : 500, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: "5px",
                  transition: "all 0.2s",
                  boxShadow: eraseMode ? "0 0 0 2px rgba(99,102,241,0.15)" : "none",
                }}
                onMouseEnter={e => { if (!eraseMode) e.currentTarget.style.background = "#f1f5f9"; }}
                onMouseLeave={e => { if (!eraseMode) e.currentTarget.style.background = "#fff"; }}
              >
                <i className="fa-solid fa-eraser" style={{ fontSize: "0.8rem" }} />
                {eraseMode ? "Eraser ON" : "Eraser"}
              </button>

              {/* Brush size */}
              {eraseMode && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                  <span style={{ fontSize: "0.65rem", color: "#94a3b8" }}>Size</span>
                  <input
                    type="range"
                    min="2"
                    max="60"
                    step="1"
                    value={eraserSize}
                    onChange={e => setEraserSize(parseInt(e.target.value))}
                    disabled={isSaving}
                    style={{ width: "65px", accentColor: "var(--color-1)", cursor: "pointer" }}
                  />
                  <span style={{
                    fontSize: "0.65rem", color: "#64748b", fontVariantNumeric: "tabular-nums",
                    minWidth: "20px", textAlign: "center",
                  }}>
                    {eraserSize}
                  </span>
                </div>
              )}

              {/* Apply Erase button */}
              {eraseStrokes.length > 0 && (
                <button
                  type="button"
                  onClick={applyEraseMaskToImage}
                  disabled={isSaving || isApplyingErase}
                  style={{
                    padding: "4px 10px", borderRadius: "6px",
                    border: "1px solid #d1d5db", background: "#f0fdf4", color: "#16a34a",
                    fontSize: "0.72rem", fontWeight: 600, cursor: (isSaving || isApplyingErase) ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", gap: "4px",
                    transition: "background 0.15s",
                    opacity: (isSaving || isApplyingErase) ? 0.6 : 1,
                  }}
                  onMouseEnter={e => { if (!isSaving && !isApplyingErase) e.currentTarget.style.background = "#dcfce7"; }}
                  onMouseLeave={e => e.currentTarget.style.background = "#f0fdf4"}
                >
                  {isApplyingErase ? (
                    <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: "0.65rem" }} />
                  ) : (
                    <i className="fa-solid fa-eraser" style={{ fontSize: "0.65rem" }} />
                  )}
                  {isApplyingErase ? "Applying..." : "Apply Erase"}
                </button>
              )}

              {/* Undo last stroke */}
              {eraseStrokes.length > 0 && (
                <button
                  type="button"
                  onClick={handleEraseUndo}
                  disabled={isSaving}
                  style={{
                    padding: "4px 10px", borderRadius: "6px",
                    border: "1px solid #d1d5db", background: "#fff", color: "#374151",
                    fontSize: "0.72rem", fontWeight: 500, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "4px",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                  onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >
                  <i className="fa-solid fa-undo" style={{ fontSize: "0.65rem" }} /> Undo
                </button>
              )}

              {/* Clear all erases */}
              {eraseStrokes.length > 0 && (
                <button
                  type="button"
                  onClick={handleEraseReset}
                  disabled={isSaving}
                  style={{
                    padding: "4px 10px", borderRadius: "6px",
                    border: "1px solid #fca5a5", background: "#fff", color: "#ef4444",
                    fontSize: "0.72rem", fontWeight: 500, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "4px",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
                  onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >
                  <i className="fa-solid fa-trash-can" style={{ fontSize: "0.65rem" }} /> Reset
                </button>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "stretch" }}>
          {/* Main Editor */}
          <div style={{ flex: "1 1 500px", minWidth: 0 }}>
            {isImgLoading && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "4px solid #f3f4f6",
                    borderTop: "4px solid #4f46e5",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            )}

            {/* Outer wrapper: flex-center keeps the image always in the middle */}
            <div
              ref={outerRef}
              style={{
                display: isImgLoading ? "none" : "block",
                overflow: "hidden",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                boxShadow: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)",
                backgroundColor: (bgRemoved || erasedImageUrl) ? "transparent" : "#f3f4f6",
                backgroundImage: (bgRemoved || erasedImageUrl)
                  ? "linear-gradient(45deg, #e2e8f0 25%, transparent 25%), linear-gradient(-45deg, #e2e8f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e2e8f0 75%), linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)"
                  : "none",
                backgroundSize: (bgRemoved || erasedImageUrl) ? "16px 16px" : "auto",
                backgroundPosition: (bgRemoved || erasedImageUrl) ? "0 0, 0 8px, 8px -8px, -8px 0px" : "0 0",
                height: "60vh",
                minHeight: "300px",
                position: "relative",
                cursor: eraseMode ? "none" : "auto",
              }}
              onMouseDown={eraseMode ? handleEraseMouseDown : undefined}
              onTouchStart={eraseMode ? handleEraseTouchStart : undefined}
              onMouseMove={(e) => {
                if (eraseMode) setBrushCursorPos({ x: e.clientX, y: e.clientY });
              }}
              onMouseEnter={(e) => {
                if (eraseMode) setBrushCursorPos({ x: e.clientX, y: e.clientY });
              }}
              onMouseLeave={() => {
                if (eraseMode && !isErasingRef.current) setBrushCursorPos(null);
              }}
            >
              <div
                ref={containerRef}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transformOrigin: "0 0",
                  transform: `scale(${displayZoom}) translate(-${displayCenter.x}%, -${displayCenter.y}%)`,
                  transition: isDragging ? "none" : "transform 0.15s ease-out",
                }}
              >
                <img
                  ref={imgRef}
                  src={erasedImageUrl || (bgRemoved && processedImageUrl ? processedImageUrl : baseImgSrc)}
                  alt="Page containing diagram"
                  onLoad={(e) => {
                    handleImageLoad(e);
                    // Ensure the mask canvas has correct dimensions as soon as image loads
                    ensureEraseMask();
                  }}
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    userSelect: "none",
                    pointerEvents: "none",
                    transform: rotation !== 0 ? `rotate(${rotation}deg)` : "none",
                    transition: "transform 0.15s ease-out",
                  }}
                />

                {/* Visible Eraser Mask Canvas (overlays the image) */}
                <canvas
                  ref={eraseMaskCanvasRef}
                  style={{
                    display: eraseMode ? "block" : "none",
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                    opacity: 0.5,
                    transform: rotation !== 0 ? `rotate(${rotation}deg)` : "none",
                    transition: "transform 0.15s ease-out",
                  }}
                />

                {!isImgLoading && (
                  <div
                    style={{
                      position: "absolute",
                      left: `${crop.x}%`,
                      top: `${crop.y}%`,
                      width: `${crop.width}%`,
                      height: `${crop.height}%`,
                      border: "1px solid #fb923c",
                      boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
                      cursor: "move",
                      boxSizing: "border-box",
                      pointerEvents: eraseMode ? "none" : "auto",
                    }}
                    onMouseDown={(e) => handleMouseDown(e, "move")}
                    onTouchStart={(e) => handleTouchStart(e, "move")}
                  >
                    {/* Corner Handles */}
                    <div
                      style={handleStyle("nwse-resize", "-5px", "-5px", null, null, null)}
                      onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, "nw"); }}
                      onTouchStart={(e) => { e.stopPropagation(); handleTouchStart(e, "nw"); }}
                    />
                    <div
                      style={handleStyle("nesw-resize", "-5px", null, "-5px", null, null)}
                      onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, "ne"); }}
                      onTouchStart={(e) => { e.stopPropagation(); handleTouchStart(e, "ne"); }}
                    />
                    <div
                      style={handleStyle("nesw-resize", null, "-5px", null, "-5px", null)}
                      onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, "sw"); }}
                      onTouchStart={(e) => { e.stopPropagation(); handleTouchStart(e, "sw"); }}
                    />
                    <div
                      style={handleStyle("nwse-resize", null, null, "-5px", "-5px", null)}
                      onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, "se"); }}
                      onTouchStart={(e) => { e.stopPropagation(); handleTouchStart(e, "se"); }}
                    />

                    {/* Edge Handles */}
                    <div
                      style={handleStyle("ns-resize", "-5px", "50%", null, null, "translateX(-50%)")}
                      onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, "n"); }}
                      onTouchStart={(e) => { e.stopPropagation(); handleTouchStart(e, "n"); }}
                    />
                    <div
                      style={handleStyle("ns-resize", null, "50%", null, "-5px", "translateX(-50%)")}
                      onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, "s"); }}
                      onTouchStart={(e) => { e.stopPropagation(); handleTouchStart(e, "s"); }}
                    />
                    <div
                      style={handleStyle("ew-resize", "50%", "-5px", null, null, "translateY(-50%)")}
                      onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, "w"); }}
                      onTouchStart={(e) => { e.stopPropagation(); handleTouchStart(e, "w"); }}
                    />
                    <div
                      style={handleStyle("ew-resize", "50%", null, "-5px", null, "translateY(-50%)")}
                      onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, "e"); }}
                      onTouchStart={(e) => { e.stopPropagation(); handleTouchStart(e, "e"); }}
                    />
                  </div>
                )}
              </div>

              {/* Eraser cursor (must be outside transformed containerRef so position: fixed works relative to viewport) */}
              {eraseMode && brushCursorPos && (
                <div
                  style={{
                    position: "fixed",
                    left: brushCursorPos.x,
                    top: brushCursorPos.y,
                    width: `${eraserSize * 2}px`,
                    height: `${eraserSize * 2}px`,
                    borderRadius: "50%",
                    border: "2px solid rgba(239, 68, 68, 0.8)",
                    backgroundColor: "rgba(239, 68, 68, 0.15)",
                    transform: "translate(-50%, -50%)",
                    pointerEvents: "none",
                    zIndex: 9999,
                  }}
                />
              )}
            </div> {/* end clip wrapper */}
          </div>

          {/* Right Side Panel: Question Info + Crop Preview */}
          {(isLibraryQuestion || showPreview) && !isImgLoading && (
            <div
              style={{
                flex: "0 0 420px",
                display: "flex",
                flexDirection: "column",
                gap: "1.25rem",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "1.25rem",
                backgroundColor: "#ffffff",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
                alignSelf: "stretch",
                height: "60vh",
                maxHeight: "60vh",
                overflowY: "auto",
              }}
            >
              {/* Crop Diagram Preview */}
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <i className="fa-solid fa-image" style={{ color: "var(--color-1)", fontSize: "0.95rem" }}></i>
                  <h4 style={{ margin: 0, fontSize: "0.9rem", fontWeight: "600", color: "#374151" }}>
                    Cropped Diagram Preview
                  </h4>
                </div>
                {/* Aspect Ratio Container Frame */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "220px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    backgroundColor: "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {(() => {
                    const cropWidth = crop.width || 1;
                    const cropHeight = crop.height || 1;
                    const cropAspectRatio = (cropWidth / cropHeight) * imageAspectRatio;
                    const isWidthConstrained = cropAspectRatio >= (380 / 220);
                    return (
                      <div
                        style={{
                          position: "relative",
                          width: isWidthConstrained ? "100%" : "auto",
                          height: isWidthConstrained ? "auto" : "100%",
                          aspectRatio: `${cropAspectRatio}`,
                          overflow: "hidden",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }}
                      >
                        <img
                          src={erasedImageUrl || (bgRemoved && processedImageUrl ? processedImageUrl : baseImgSrc)}
                          alt="Cropped Preview"
                          style={{
                            position: "absolute",
                            left: `-${(crop.x / cropWidth) * 100}%`,
                            top: `-${(crop.y / cropHeight) * 100}%`,
                            width: `${(100 / cropWidth) * 100}%`,
                            height: `${(100 / cropHeight) * 100}%`,
                            display: "block",
                            pointerEvents: "none",
                            transform: rotation !== 0 ? `rotate(${rotation}deg)` : "none",
                          }}
                        />
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Options & Correct Option / Answer */}
              {(() => {
                const hasOptions = question?.options && question.options.filter(Boolean).length > 0;
                if (hasOptions) {
                  return (
                    <>
                      {/* Options */}
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                        <h4 style={{ margin: 0, fontSize: "0.9rem", fontWeight: "600", color: "#374151" }}>
                          Options
                        </h4>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                          {question.options.filter(Boolean).map((opt, oi) => {
                            const optLabel = (opt && typeof opt === 'object') ? opt.key : (opt ? opt.charAt(0) : '');
                            const optText = (opt && typeof opt === 'object') ? `${opt.key}) ${opt.text}` : (opt || '');
                            const isCorrect =
                              question.correctOptions &&
                              question.correctOptions.includes(optLabel);
                            return (
                              <div
                                key={oi}
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "0.4rem",
                                  fontSize: "0.8rem",
                                  color: isCorrect ? "#16a34a" : "#475569",
                                  fontWeight: isCorrect ? 600 : 400,
                                  background: isCorrect ? "#f0fdf4" : "#f8fafc",
                                  padding: "6px 10px",
                                  borderRadius: "8px",
                                  border: isCorrect ? "1px solid #bbf7d0" : "1px solid #e2e8f0",
                                  minHeight: "38px",
                                }}
                              >
                                <span style={{ lineHeight: 1.4, width: "100%", overflow: "hidden" }}>
                                  <ReactMarkdown
                                    children={formatLatexText(optText)}
                                    remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                    rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                                    components={{ p: "span" }}
                                  />
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Correct Option Badges */}
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", borderTop: "1px dashed #e2e8f0", paddingTop: "0.75rem" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#475569" }}>
                            Correct Option:
                          </span>
                          <div style={{ display: "flex", gap: "0.4rem" }}>
                            {["A", "B", "C", "D"].map((optLabel) => {
                              const isCorrect =
                                question?.correctOptions &&
                                question.correctOptions.includes(optLabel);
                              return (
                                <span
                                  key={optLabel}
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    border: isCorrect ? "1px solid #16a34a" : "1px solid #cbd5e1",
                                    background: isCorrect ? "#16a34a" : "#ffffff",
                                    color: isCorrect ? "#ffffff" : "#64748b",
                                  }}
                                >
                                  {optLabel}
                                </span>
                              );
                            })}
                          </div>
                        </div>

                        {/* Mapped Correct Answer Text */}
                        {(() => {
                          const correctAnswers = [];
                          if (question?.options && question?.correctOptions) {
                            question.options.filter(Boolean).forEach((opt) => {
                              const optLabel = (opt && typeof opt === 'object') ? opt.key : (opt ? opt.charAt(0) : '');
                              if (question.correctOptions.includes(optLabel)) {
                                const optText = (opt && typeof opt === 'object') ? opt.text : (opt || '');
                                let displayedText = optText;
                                if (typeof optText === 'string' && optText.includes(')')) {
                                  displayedText = optText.split(')').slice(1).join(')').trim();
                                }
                                correctAnswers.push({ label: optLabel, text: displayedText });
                              }
                            });
                          }

                          if (correctAnswers.length > 0) {
                            return (
                              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                                <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#475569" }}>
                                  Correct Answer:
                                </span>
                                {correctAnswers.map((ans, idx) => (
                                  <div
                                    key={idx}
                                    style={{
                                      fontSize: "0.8rem",
                                      color: "#16a34a",
                                      fontWeight: 600,
                                      background: "#f0fdf4",
                                      padding: "8px 12px",
                                      borderRadius: "8px",
                                      border: "1px solid #bbf7d0",
                                    }}
                                  >
                                    <ReactMarkdown
                                      children={formatLatexText(ans.text)}
                                      remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                      rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                                      components={{ p: "span" }}
                                    />
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </>
                  );
                } else {
                  // Numerical/No Options -> Show Correct Answer
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", borderTop: "1px dashed #e2e8f0", paddingTop: "0.75rem" }}>
                      <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#475569" }}>
                        Correct Answer:
                      </span>
                      <div
                        style={{
                          fontSize: "0.85rem",
                          color: "#16a34a",
                          fontWeight: 600,
                          background: "#f0fdf4",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border: "1px solid #bbf7d0",
                        }}
                      >
                        {question?.correctAnswer ? (
                          <ReactMarkdown
                            children={formatLatexText(question.correctAnswer)}
                            remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                            rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                            components={{ p: "span" }}
                          />
                        ) : (
                          <span style={{ fontStyle: "italic", color: "#94a3b8" }}>No correct answer specified</span>
                        )}
                      </div>
                    </div>
                  );
                }
              })()}
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.5rem" }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            style={{
              padding: "0.5rem 1rem",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              backgroundColor: "#ffffff",
              color: "#374151",
              fontSize: "0.875rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || isImgLoading}
            style={{
              padding: "0.5rem 1rem",
              borderRadius: "6px",
              border: "none",
              background: isSaving
                ? "#9ca3af"
                : "var(--color-1)",
              color: "#ffffff",
              fontSize: "0.875rem",
              fontWeight: "500",
              cursor: isSaving || isImgLoading ? "not-allowed" : "pointer",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              transition: "opacity 0.2s",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onMouseEnter={(e) => {
              if (!isSaving && !isImgLoading) e.currentTarget.style.opacity = "0.9";
            }}
            onMouseLeave={(e) => {
              if (!isSaving && !isImgLoading) e.currentTarget.style.opacity = "1";
            }}
          >
            {isSaving ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                Saving Diagram...
              </>
            ) : (
              "Save Diagram"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
